/**
 * <hh-solitaire-scatter> — decorative Solitaire cards with None / Toss / Shake.
 *
 * Cards are rendered by Liquid (`solitaire-card` blocks). This element only
 * handles interaction: flick physics for Toss, a hover/tap wiggle for Shake.
 */

const TOSS = {
  torque: 1.2e-5,
  spinDamping: 140,
  glide: 260,
  spinGlide: 200,
  maxSpin: 0.18,
  maxSpeed: 3.2,
  bounce: 0.4,
  overhang: 0.2,
  liftScale: 1.05,
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

class HHSolitaireScatter extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return
    this.initialized = true
    this.active = new Set()
    this.hold = null
    this.stack = 10
    this.cards().forEach((el, i) => el.style.setProperty('--i', i))

    const mode = this.mode
    if (mode === 'none' || reducedMotion.matches) return
    if (this.designMode && mode === 'toss') return

    this.onPointerDown = this.onPointerDown.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)
    this.onPointerOver = this.onPointerOver.bind(this)
    this.addEventListener('pointerdown', this.onPointerDown)
    this.addEventListener('pointerover', this.onPointerOver)
    window.addEventListener('pointermove', this.onPointerMove, { passive: true })
    window.addEventListener('pointerup', this.onPointerUp)
    window.addEventListener('pointercancel', this.onPointerUp)

    this.resizeObserver = new ResizeObserver(() => this.onResize())
    this.resizeObserver.observe(this)
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onPointerDown)
    this.removeEventListener('pointerover', this.onPointerOver)
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('pointercancel', this.onPointerUp)
    this.resizeObserver?.disconnect()
    cancelAnimationFrame(this.frame)
  }

  get mode() {
    return this.dataset.animation || 'none'
  }

  get designMode() {
    return Boolean(window.Shopify?.designMode) || Boolean(this.closest('[data-design-mode]'))
  }

  get game() {
    return this.closest('hh-solitaire')
  }

  cards() {
    return [...this.querySelectorAll('.sol-scatter__card')]
  }

  onPointerDown(e) {
    const el = e.target.closest('.sol-scatter__card')
    if (!el || !this.contains(el)) return
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return

    if (this.mode === 'shake') {
      this.shake(el, { restart: true })
      return
    }

    if (this.mode !== 'toss' || this.hold) return
    if (this.game && this.game.screen && this.game.screen !== 'onboarding') return

    e.preventDefault()
    this.sounds?.unlock?.()

    const card = this.take(el)
    const origin = this.getBoundingClientRect()
    const px = e.clientX - origin.left
    const py = e.clientY - origin.top
    const rad = (-card.r * Math.PI) / 180
    const dx = px - card.x
    const dy = py - card.y
    card.hold = {
      pointerId: e.pointerId,
      origin,
      local: { x: dx * Math.cos(rad) - dy * Math.sin(rad), y: dx * Math.sin(rad) + dy * Math.cos(rad) },
      px,
      py,
      samples: [[performance.now(), px, py]],
    }
    card.vx = card.vy = card.vr = 0
    this.hold = card
    el.style.zIndex = String(++this.stack)
    el.classList.add('is-lifted')
    try {
      el.setPointerCapture(e.pointerId)
    } catch {
      // Moves and release are tracked on window, so capture is only a nicety.
    }
    this.dataset.tossing = ''
    this.game?.toggleAttribute?.('data-tossing', true)
    this.sounds?.play?.('deal', { volume: 0.6 })
    this.animate(card)
  }

  /** Hovering in with a mouse plays one full wiggle, even if the pointer leaves before it ends. */
  onPointerOver(e) {
    if (this.mode !== 'shake' || e.pointerType !== 'mouse') return
    const el = e.target.closest('.sol-scatter__card')
    if (!el || !this.contains(el) || el.contains(e.relatedTarget)) return
    this.shake(el)
  }

  shake(el, { restart = false } = {}) {
    if (el.classList.contains('is-shaking') && !restart) return
    clearTimeout(el.shakeTimer)
    el.classList.remove('is-shaking')
    void el.offsetWidth
    el.classList.add('is-shaking')
    el.shakeTimer = setTimeout(() => el.classList.remove('is-shaking'), 700)
  }

  onPointerMove(e) {
    const card = this.hold
    if (!card || e.pointerId !== card.hold.pointerId) return
    const hold = card.hold
    const now = performance.now()
    hold.px = e.clientX - hold.origin.left
    hold.py = e.clientY - hold.origin.top
    hold.samples.push([now, hold.px, hold.py])
    while (hold.samples.length > 2 && now - hold.samples[0][0] > 90) hold.samples.shift()
  }

  onPointerUp(e) {
    const card = this.hold
    if (!card || e.pointerId !== card.hold.pointerId) return
    const velocity = e.type === 'pointercancel' ? { x: 0, y: 0 } : this.velocity(card.hold.samples)
    const speed = Math.hypot(velocity.x, velocity.y)
    const limit = speed > TOSS.maxSpeed ? TOSS.maxSpeed / speed : 1
    card.vx = reducedMotion.matches ? 0 : velocity.x * limit
    card.vy = reducedMotion.matches ? 0 : velocity.y * limit
    card.vr = reducedMotion.matches ? 0 : clamp(card.vr, -TOSS.maxSpin, TOSS.maxSpin)
    card.hold = null
    card.tossed = speed > 0.5 && !reducedMotion.matches
    this.hold = null
    delete this.dataset.tossing
    this.game?.removeAttribute?.('data-tossing')
    card.el.classList.remove('is-lifted')
    this.sounds?.play?.(card.tossed ? 'flip' : 'move', { volume: card.tossed ? 0.5 : 0.7 })
    this.animate(card)
  }

  get sounds() {
    return this.game?.sounds
  }

  take(el) {
    if (el.scatter) return el.scatter
    const wrap = this.getBoundingClientRect()
    const rect = el.getBoundingClientRect()
    const matrix = new DOMMatrixReadOnly(getComputedStyle(el).transform)
    const card = {
      el,
      x: rect.left + rect.width / 2 - wrap.left,
      y: rect.top + rect.height / 2 - wrap.top,
      r: (Math.atan2(matrix.b, matrix.a) * 180) / Math.PI,
      w: el.offsetWidth,
      h: el.offsetHeight,
      scale: 1,
      vx: 0,
      vy: 0,
      vr: 0,
      hold: null,
    }
    el.scatter = card
    el.dataset.free = ''
    el.style.left = '0px'
    el.style.top = '0px'
    this.apply(card)
    return card
  }

  apply(card) {
    card.el.style.transform = `translate3d(${card.x - card.w / 2}px, ${card.y - card.h / 2}px, 0) rotate(${card.r}deg) scale(${card.scale})`
  }

  velocity(samples) {
    const first = samples[0]
    const last = samples[samples.length - 1]
    if (performance.now() - last[0] > 60) return { x: 0, y: 0 }
    const dt = Math.max(16, last[0] - first[0])
    return { x: (last[1] - first[1]) / dt, y: (last[2] - first[2]) / dt }
  }

  animate(card) {
    this.active.add(card)
    if (!this.frame) this.frame = requestAnimationFrame((t) => this.step(t))
  }

  step(now) {
    const dt = clamp(now - (this.time ?? now - 16), 1, 32)
    this.time = now
    const width = this.clientWidth
    const height = this.clientHeight

    for (const card of this.active) {
      const hold = card.hold
      if (hold) {
        if (!reducedMotion.matches) {
          const v = this.velocity(hold.samples)
          const rad = (card.r * Math.PI) / 180
          const ox = hold.local.x * Math.cos(rad) - hold.local.y * Math.sin(rad)
          const oy = hold.local.x * Math.sin(rad) + hold.local.y * Math.cos(rad)
          card.vr += (ox * v.y - oy * v.x) * TOSS.torque * dt
          card.vr *= Math.exp(-dt / TOSS.spinDamping)
          card.r += card.vr * dt
        }
        const rad = (card.r * Math.PI) / 180
        card.x = hold.px - (hold.local.x * Math.cos(rad) - hold.local.y * Math.sin(rad))
        card.y = hold.py - (hold.local.x * Math.sin(rad) + hold.local.y * Math.cos(rad))
        card.scale += (TOSS.liftScale - card.scale) * (1 - Math.exp(-dt / 70))
      } else {
        card.x += card.vx * dt
        card.y += card.vy * dt
        card.r += card.vr * dt
        const glide = Math.exp(-dt / TOSS.glide)
        card.vx *= glide
        card.vy *= glide
        card.vr *= Math.exp(-dt / TOSS.spinGlide)
        card.scale += (1 - card.scale) * (1 - Math.exp(-dt / 90))

        const minX = -card.w * TOSS.overhang
        const maxX = width + card.w * TOSS.overhang
        const minY = -card.h * TOSS.overhang
        const maxY = height + card.h * TOSS.overhang
        if (card.x < minX || card.x > maxX) {
          card.x = clamp(card.x, minX, maxX)
          card.vx *= -TOSS.bounce
        }
        if (card.y < minY || card.y > maxY) {
          card.y = clamp(card.y, minY, maxY)
          card.vy *= -TOSS.bounce
        }

        const resting = Math.hypot(card.vx, card.vy) < 0.01 && Math.abs(card.vr) < 0.003 && Math.abs(card.scale - 1) < 0.002
        if (resting) {
          card.scale = 1
          this.active.delete(card)
          if (card.tossed) this.sounds?.play?.('move', { volume: 0.45 })
          card.tossed = false
        }
      }
      this.apply(card)
    }

    if (this.active.size) {
      this.frame = requestAnimationFrame((t) => this.step(t))
    } else {
      this.frame = null
      this.time = null
    }
  }

  onResize() {
    cancelAnimationFrame(this.resizeFrame)
    this.resizeFrame = requestAnimationFrame(() => {
      const mobile = this.clientWidth < 750
      if (this.layout != null && this.layout !== mobile) this.reset()
      this.layout = mobile
      this.querySelectorAll('.sol-scatter__card[data-free]').forEach((el) => {
        const card = el.scatter
        if (!card) return
        card.x = clamp(card.x, -card.w * TOSS.overhang, this.clientWidth + card.w * TOSS.overhang)
        card.y = clamp(card.y, -card.h * TOSS.overhang, this.clientHeight + card.h * TOSS.overhang)
        this.apply(card)
      })
    })
  }

  reset() {
    this.active.clear()
    this.hold = null
    delete this.dataset.tossing
    this.game?.removeAttribute?.('data-tossing')
    this.cards().forEach((el) => {
      delete el.scatter
      delete el.dataset.free
      el.classList.remove('is-lifted', 'is-shaking')
      el.style.left = ''
      el.style.top = ''
      el.style.transform = ''
      el.style.zIndex = ''
    })
  }
}

if (!customElements.get('hh-solitaire-scatter')) {
  customElements.define('hh-solitaire-scatter', HHSolitaireScatter)
}
